import { useState } from "react";
import { LifeBuoy, Loader2, CheckCircle2, MessageCircle, RefreshCw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Navbar } from "@/components/customer/Navbar";
import { Footer } from "@/components/customer/Footer";
import { WhatsAppButton } from "@/components/customer/WhatsAppButton";
import { api, errMsg } from "@/lib/api";
import { useSettings, waLink } from "@/context/SettingsContext";

const SUBJECTS = ["Caída de seguidores", "Pedido no entregado", "Problema con el pago / comprobante", "Consulta general", "Otro"];

const Support = () => {
  const s = useSettings();
  const [form, setForm] = useState({ instagram_username: "", whatsapp: "", email: "", subject: "Consulta general", message: "", order_number: "" });
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);

  const submit = async () => {
    if (!form.instagram_username.trim()) return toast.error("Ingresá tu usuario de Instagram");
    if (!form.whatsapp.trim()) return toast.error("Ingresá tu WhatsApp");
    if (!form.message.trim()) return toast.error("Escribí tu mensaje");
    setLoading(true);
    try {
      const payload = { ...form, order_number: form.order_number ? Number(form.order_number) : null };
      const { data } = await api.post("/tickets", payload);
      setTicket(data);
      toast.success("¡Ticket creado!");
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 pb-16">
        <div className="max-w-2xl mx-auto px-5">
          <div className="text-center mb-8">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-indigo-50 flex items-center justify-center mb-3"><LifeBuoy className="h-6 w-6 text-indigo-600" /></div>
            <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-900">Soporte</h1>
            <p className="mt-3 text-slate-600">¿Tuviste algún problema? Abrí un ticket sin necesidad de registrarte.</p>
          </div>

          {/* Refill highlight */}
          <div className="rounded-3xl border border-slate-100 bg-slate-50 p-5 mb-6 flex items-start gap-4 soft-shadow" data-testid="refill-card">
            <div className="h-11 w-11 rounded-xl bg-pink-100 flex items-center justify-center shrink-0"><RefreshCw className="h-5 w-5 text-pink-600" /></div>
            <div className="flex-1">
              <p className="font-heading font-bold text-slate-900">¿Caída de seguidores?</p>
              <p className="text-sm text-slate-500 mt-1">Después de la entrega puede ocurrir una caída natural. Pedí tu reposición directamente en el sitio de reposición.</p>
              <a href={s.refill_url || "https://refil.perfilturbinado.com/"} target="_blank" rel="noreferrer" data-testid="refill-btn">
                <Button className="mt-3 rounded-full bg-slate-900 hover:bg-indigo-600 text-white font-semibold h-11 px-5 active:scale-95 transition-all">
                  Solicitar reposición <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>

          {ticket ? (
            <div className="bg-white rounded-3xl p-8 soft-shadow border border-slate-100 text-center" data-testid="ticket-success">
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4"><CheckCircle2 className="h-7 w-7 text-emerald-600" /></div>
              <h2 className="font-heading font-extrabold text-2xl text-slate-900">¡Ticket #{ticket.ticket_number} creado!</h2>
              <p className="text-slate-500 mt-1">Nuestro equipo revisará tu caso a la brevedad.</p>
              <a href={waLink(s.whatsapp, `Hola, abrí el ticket #${ticket.ticket_number} en Aumenta Ya.`)} target="_blank" rel="noreferrer">
                <Button className="mt-6 w-full rounded-full h-12 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold"><MessageCircle className="mr-2 h-5 w-5" /> Contactar por WhatsApp</Button>
              </a>
              <Button variant="ghost" onClick={() => { setTicket(null); setForm({ instagram_username: "", whatsapp: "", email: "", subject: "Consulta general", message: "", order_number: "" }); }} className="mt-2 w-full rounded-full h-11">Abrir otro ticket</Button>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-6 soft-shadow border border-slate-100 space-y-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Motivo</Label>
                <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                  <SelectTrigger className="rounded-xl mt-1.5 h-12" data-testid="ticket-subject"><SelectValue /></SelectTrigger>
                  <SelectContent>{SUBJECTS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Usuario de Instagram *</Label>
                <Input value={form.instagram_username} onChange={(e) => setForm({ ...form, instagram_username: e.target.value })} placeholder="@tuusuario" className="rounded-xl mt-1.5 h-12" data-testid="ticket-username" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-700">WhatsApp *</Label>
                  <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+595 9XX XXXXXX" className="rounded-xl mt-1.5 h-12" data-testid="ticket-whatsapp" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700">N° de pedido</Label>
                  <Input value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} placeholder="Opcional" className="rounded-xl mt-1.5 h-12" data-testid="ticket-order" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Mensaje *</Label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Contanos qué pasó..." className="rounded-xl mt-1.5 min-h-[110px]" data-testid="ticket-message" />
              </div>
              <Button onClick={submit} disabled={loading} className="w-full rounded-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold active:scale-95 transition-all" data-testid="ticket-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enviar ticket"}
              </Button>
            </div>
          )}
        </div>
      </main>
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default Support;
